import { LocalDate, nativeJs } from "@js-joda/core";

export interface IInterval {
    DateFrom: Date;
    DateTo: Date | null;
}

/**
 * A class representing an interval on the calendar.
 */
export class DateInterval {
    private readonly _end: LocalDate;

    constructor(private readonly _start: LocalDate, end?: LocalDate) {
        this._end = end || LocalDate.now();

        if (this._end.isBefore(this._start))
            throw "The start of the DateInterval must come before the end of the DateInterval";
    }

    static from(interval: IInterval) {
        const start = nativeJs(interval.DateFrom).toLocalDate();

        if (!interval.DateTo)
            return new DateInterval(start);
        
        const end = nativeJs(interval.DateTo).toLocalDate();

        return new DateInterval(start, end);
    }

    get start(): LocalDate {
        return this._start;
    }

    get end(): LocalDate {
        return this._end;
    }

    /**
     * Checks whether this interval overlaps with the provided interval
     * @param other - The other interval
     * @returns true when the intervals overlap (not abutting)
     */
    overlaps(other: DateInterval): boolean {
        if (this._start.isAfter(other._end))
            return false;
        if (this._end.isBefore(other._start))
            return false;

        return true;
    }

    /**
     * Checks whether this interval abuts the other provided interval.
     * Intervals abut when they touch but do not overlap, 
     * this means that the start time one of the intervals is exactly 1 day later than the end time of the other interval.
     * @param other - The other interval
     * @returns True if the intervals are abutting
     */
    abuts(other: DateInterval): boolean {
        if (this._start.minusDays(1).equals(other._end))
            return true;
        if (this._end.plusDays(1).equals(other._start))
            return true;
        return false;
    }

    /**
     * Determines whether this interval covers the entirety of the provided interval.
     * An interval is covered when this.Start is no later than other.Start and
     * this.End is no earlier than other.End
     * @param other - other interval
     * @returns True when other is covered by this interval
     */
    covers(other: DateInterval): boolean {
        if (this._start.isAfter(other._start))
            return false;
        if (this._end.isBefore(other._end))
            return false;

        return true;
    }

    /**
     * Creates a copy to ensure we do not modify the existing instance
     * @returns A copy of the interval
     */
    copy(): DateInterval {
        return new DateInterval(this._start, this._end);
    }

    /**
     * Creates a merged interval without modifying the current instances.
     * The earliest start date and the latest end date will be used to instantiate the new interval.
     * @remarks This does not check if the intervals are abutting or overlapping.
     * @param other - The other interval
     * @returns A new interval which covers both intervals
     */
    span(other: DateInterval): DateInterval {
        const startTime = other._start.isBefore(this._start) ? other.start : this.start;
        const endTime = other._end.isAfter(this._end) ? other._end : this._end;
        return new DateInterval(startTime, endTime);
    }

    toString(): string {
        return `[${this._start}, ${this._end}]`;
    }
}